import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RefreshCw, AlertTriangle, CircleDot, Radio, Trophy, Clock, Zap, ShieldCheck, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { fmtMoney, fmtNum, fmtHora, fmtFecha, fmtProximoLocal } from "@/lib/format";

const STATUS = {
  verde: { color: "#4ECDC4", label: "Fresco" },
  ambar: { color: "#F39C12", label: "Esperando ciclo" },
  rojo: { color: "#FF6B5B", label: "Falla / Cuarentena" },
};

function countdown(targetISO, now) {
  if (!targetISO) return { txt: "—", danger: false };
  const diff = new Date(targetISO).getTime() - now;
  if (diff <= 0) return { txt: "EN SORTEO", danger: false };
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  const txt = d > 0 ? `${d}d ${p(h)}:${p(m)}:${p(sec)}` : `${p(h)}:${p(m)}:${p(sec)}`;
  return { txt, danger: s < 3600 };
}

const Ball = ({ n, extra }) => (
  <span
    className={`inline-flex items-center justify-center rounded-full font-mono-ax font-bold text-[13px] leading-none w-[22px] h-[22px] ${
      extra
        ? "text-white bg-gradient-to-b from-[#FFB347] via-[#FF6B2F] to-[#FF2D1F]"
        : "text-[#0A0A0A] bg-gradient-to-b from-[#FFFFFF] to-[#C4C4CE]"
    }`}
    style={extra
      ? { boxShadow: "0 0 8px 1px rgba(255,86,47,0.55)" }
      : { boxShadow: "inset 0 -2px 3px rgba(0,0,0,0.25)" }}
  >{n}</span>
);

const EXTRA_REQUERIDA = {
  powerball: "Powerball", megamillions: "Mega Ball", lottoamerica: "Star Ball",
  ca_superlottoplus: "Mega", ny_lotto: "Bonus", tx_twostep: "Bonus Ball",
  tx_pick3: "Fireball", tx_daily4: "Fireball",
  fl_pick2: "Fireball", fl_pick3: "Fireball", fl_pick4: "Fireball", fl_pick5: "Fireball",
  il_pick3: "Fireball", il_pick4: "Fireball", nc_pick3: "Fireball", nc_pick4: "Fireball",
  pa_pick2: "Wild Ball", pa_pick3: "Wild Ball", pa_pick4: "Wild Ball", pa_pick5: "Wild Ball",
};

const ESTADOS = {
  ca: ["CA", "California"], tx: ["TX", "Texas"], fl: ["FL", "Florida"], ny: ["NY", "Nueva York"],
  oh: ["OH", "Ohio"], il: ["IL", "Illinois"], pa: ["PA", "Pensilvania"], mi: ["MI", "Míchigan"],
  ga: ["GA", "Georgia"], nc: ["NC", "Carolina del Norte"],
};
const NACIONALES = new Set(["powerball", "megamillions", "lottoamerica"]);

export const estadoDe = (gameId = "") => {
  if (NACIONALES.has(gameId)) return ["US", "Nacional (multiestado)"];
  return ESTADOS[gameId.split("_")[0]] || null;
};

const EtiquetaEstado = ({ gameId, grande }) => {
  const e = estadoDe(gameId);
  if (!e) return null;
  return (
    <span data-testid={`estado-${gameId}`} className={`inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[#D4AF37] ${grande ? "text-xs" : "text-[10px]"}`}>
      <span className="font-mono-ax px-1 rounded bg-[#D4AF37]/15 border border-[#D4AF37]/30">{e[0]}</span>
      <span className="truncate">{e[1]}</span>
    </span>
  );
};

export const faltaExtra = (j) =>
  !!EXTRA_REQUERIDA[j?.game_id] && (j?.ultimo_sorteo?.numeros || []).length > 0 && j.ultimo_sorteo.extra == null;

const nombreExtra = (j) => j?.ultimo_sorteo?.extra_nombre || EXTRA_REQUERIDA[j?.game_id] || "Bola extra";

const BallFaltante = ({ juego }) => (
  <span data-testid={`falta-extra-ball-${juego.game_id}`} title={`Falta ${nombreExtra(juego)} — la app no la tiene registrada en este sorteo`}
    className="inline-flex items-center justify-center rounded-full font-mono-ax font-bold text-[12px] leading-none w-[22px] h-[22px] border border-dashed border-[#FF6B2F] text-[#FF6B2F] bg-[#FF6B2F]/10">?</span>
);

const AvisoFaltaExtra = ({ juego }) => (
  <div data-testid={`falta-extra-${juego.game_id}`}
    className="mt-1 flex items-center gap-1 rounded px-1 py-0.5 bg-[#F39C12]/15 border border-[#F39C12]/40">
    <AlertTriangle className="w-3 h-3 shrink-0 text-[#F39C12]" />
    <span className="truncate text-[9px] leading-tight font-semibold uppercase text-[#F39C12]">Falta {nombreExtra(juego)}</span>
  </div>
);

const Sello = ({ sello, gameId }) => {
  if (!sello) return null;
  const ok = sello.verificado_2_fuentes;
  const fuentes = (sello.fuentes || []).join(" + ");
  const texto = ok ? (sello.texto || "Verificado en 2 fuentes")
                    : "1 fuente oficial · verificación de 2ª fuente en curso";
  const Icon = ok ? ShieldCheck : ShieldAlert;
  const color = ok ? "#4ECDC4" : "#F39C12";
  return (
    <div
      data-testid={`sello-${gameId}`}
      title={fuentes ? `${texto} — ${fuentes}` : texto}
      className="mt-1 flex items-center gap-1 rounded px-1 py-0.5"
      style={{ backgroundColor: ok ? "rgba(78,205,196,0.10)" : "rgba(243,156,18,0.10)" }}
    >
      <Icon className="w-3 h-3 shrink-0" style={{ color }} />
      <span className="truncate text-[9px] leading-tight" style={{ color }}>{texto}</span>
    </div>
  );
};

const GameCell = ({ juego, now, onClick }) => {
  const st = STATUS[juego.estado] || { color: "#71717A", label: "—" };
  const cd = countdown(juego.proximo_sorteo_utc, now);
  const esRojo = juego.estado === "rojo";
  const nums = juego.ultimo_sorteo?.numeros || [];
  return (
    <button
      data-testid={`matriz-cell-${juego.game_id}`}
      onClick={onClick}
      className={`text-left rounded-lg bg-[#0A0A0A] border p-2 flex flex-col justify-between overflow-hidden hover:bg-[#121212] transition-colors ${
        esRojo ? "ax-danger-cell border-[#FF6B5B]/40" : "border-[#1F1F24]"
      }`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="min-w-0">
          <EtiquetaEstado gameId={juego.game_id} />
          <span className="block text-[17px] font-bold text-[#F5F5F7] leading-tight line-clamp-2">{juego.nombre}</span>
        </span>
        <span className="relative shrink-0 mt-0.5">
          <span className="block w-2 h-2 rounded-full ax-heartbeat" style={{ backgroundColor: st.color }} />
        </span>
      </div>

      <div className="my-1">
        <div className={`font-mono-ax text-[11px] ${cd.danger ? "text-[#FF6B5B]" : "text-[#A1A1AA]"}`}>{cd.txt}</div>
        <div className="font-mono-ax text-[11px] text-[#FFD700] truncate">
          {juego.jackpot_actual ? fmtMoney(juego.jackpot_actual) : "—"}
        </div>
        <div className="font-mono-ax text-[10px] text-[#71717A] truncate" title="Próximo sorteo en tu hora local">
          {fmtProximoLocal(juego.proximo_sorteo_utc)}
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {nums.length > 0 ? (
          <>
            {nums.map((n, i) => <Ball key={i} n={n} />)}
            {juego.ultimo_sorteo?.extra != null && <Ball n={juego.ultimo_sorteo.extra} extra />}
            {faltaExtra(juego) && <BallFaltante juego={juego} />}
          </>
        ) : <span className="text-[#52525b] text-[10px]">—</span>}
      </div>

      {faltaExtra(juego) && <AvisoFaltaExtra juego={juego} />}
      <Sello sello={juego.sello} gameId={juego.game_id} />
    </button>
  );
};

const Matriz = () => {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/comando/matriz");
      setData(data);
      setLastRefresh(new Date());
    } catch (e) {
      setData({ puente_pendiente: true });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    document.title = "Matriz · AXIORA";
    cargar();
    const refresh = setInterval(cargar, 60000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [cargar]);

  const pendiente = data?.puente_pendiente;
  const juegos = data?.juegos || [];
  const resumen = data?.resumen || { verdes: 0, ambar: 0, rojos: 0 };
  const totalSellos = resumen.total ?? juegos.length;
  const verif2 = resumen.verificados_2_fuentes ?? juegos.filter((j) => j?.sello?.verificado_2_fuentes).length;
  const sinExtra = juegos.filter(faltaExtra);

  const Counter = ({ color, label, value, testid }) => (
    <div data-testid={testid} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F24]">
      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="font-mono-ax text-lg text-[#F5F5F7]">{fmtNum(value)}</span>
      <span className="text-[10px] text-[#71717A] uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="p-6 min-h-full flex flex-col">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide">La Matriz</h2>
          <p className="text-xs text-[#71717A] mt-0.5">49 eruditos · monitoreo en vivo</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Counter color="#4ECDC4" label="Verde" value={resumen.verdes} testid="counter-verde" />
          <Counter color="#F39C12" label="Ámbar" value={resumen.ambar} testid="counter-ambar" />
          <Counter color="#FF6B5B" label="Rojo" value={resumen.rojos} testid="counter-rojo" />
          <div data-testid="counter-sellos" title="Juegos verificados en 2 fuentes independientes"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#4ECDC4]/30">
            <ShieldCheck className="w-3.5 h-3.5 text-[#4ECDC4]" />
            <span className="font-mono-ax text-lg text-[#F5F5F7]">{fmtNum(verif2)}/{fmtNum(totalSellos)}</span>
            <span className="text-[10px] text-[#71717A] uppercase tracking-wider">2 fuentes</span>
          </div>
          {sinExtra.length > 0 && (
            <div data-testid="counter-falta-extra" title="Juegos cuyo último sorteo llegó sin su bola extra"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#F39C12]/40">
              <AlertTriangle className="w-3.5 h-3.5 text-[#F39C12]" />
              <span className="font-mono-ax text-lg text-[#F5F5F7]">{fmtNum(sinExtra.length)}</span>
              <span className="text-[10px] text-[#71717A] uppercase tracking-wider">Sin bola extra</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A0A0A] border border-[#1F1F24]">
            <RefreshCw className={`w-3.5 h-3.5 text-[#D4AF37] ${loading ? "animate-spin" : ""}`} />
            <span className="font-mono-ax text-xs text-[#A1A1AA]">{lastRefresh ? fmtHora(lastRefresh) : "—"}</span>
          </div>
        </div>
      </header>

      {resumen.rojos > 0 && (
        <div data-testid="alerta-rojos" className="mb-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF6B5B]/10 border border-[#FF6B5B]/30 text-[#FF6B5B] text-sm">
          <AlertTriangle className="w-4 h-4" />
          {resumen.rojos} erudito(s) en falla o cuarentena — requieren atención.
        </div>
      )}

      {sinExtra.length > 0 && (
        <div data-testid="alerta-falta-extra" className="mb-4 flex items-start gap-2 px-4 py-2 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>FALTA BOLA EXTRA en {sinExtra.length} juego(s): {sinExtra.map((j) => `${j.nombre} (${estadoDe(j.game_id)?.[1] || j.game_id})`).join(" · ")} — la app no la tiene registrada en el último sorteo (los usuarios tampoco la ven).</span>
        </div>
      )}

      {pendiente && (
        <div data-testid="matriz-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — sin conexión con el puente de Axiora.
        </div>
      )}

      <div className="grid grid-cols-7 gap-2 flex-1" style={{ gridAutoRows: "minmax(126px, 1fr)" }}>
        {loading && !juegos.length ? (
          Array.from({ length: 49 }).map((_, i) => (
            <div key={i} className="rounded-lg bg-[#0A0A0A] border border-[#1F1F24] animate-pulse" />
          ))
        ) : juegos.length ? (
          juegos.map((j) => <GameCell key={j.game_id} juego={j} now={now} onClick={() => setSel(j)} />)
        ) : (
          Array.from({ length: 49 }).map((_, i) => (
            <div key={i} className="rounded-lg bg-[#0A0A0A] border border-[#1F1F24] flex items-center justify-center text-[#52525b] text-xs">—</div>
          ))
        )}
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="bg-[#0A0A0A] border-l border-[#1F1F24] text-[#F5F5F7] w-full sm:max-w-md overflow-y-auto" data-testid="expediente-juego">
          {sel && (
            <>
              <SheetHeader>
                <EtiquetaEstado gameId={sel.game_id} grande />
                <SheetTitle className="font-serif-ax text-2xl text-[#FFD700]">{sel.nombre}</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-5 text-sm">
                <Row icon={CircleDot} label="Estado del erudito"
                  value={<span style={{ color: STATUS[sel.estado]?.color }}>{STATUS[sel.estado]?.label || "—"}</span>} />
                <Row icon={Clock} label="Próximo sorteo (tu hora)" value={fmtProximoLocal(sel.proximo_sorteo_utc)} />
                <Row icon={Clock} label="Próximo sorteo (ET)" value={sel.proximo_sorteo_texto || "—"} />
                <Row icon={Zap} label="Días sin sorteo" value={fmtNum(sel.dias_sin_sorteo)} />
                <Row icon={Trophy} label="Jackpot actual" value={<span className="text-[#FFD700]">{sel.jackpot_actual ? fmtMoney(sel.jackpot_actual) : "—"}</span>} />

                <div className="pt-2 border-t border-[#1F1F24]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Sello de Confianza</p>
                  {sel.sello ? (
                    <div className="rounded-lg px-3 py-2.5 flex items-start gap-2"
                      style={{ backgroundColor: sel.sello.verificado_2_fuentes ? "rgba(78,205,196,0.10)" : "rgba(243,156,18,0.10)" }}>
                      {sel.sello.verificado_2_fuentes
                        ? <ShieldCheck className="w-4 h-4 text-[#4ECDC4] shrink-0 mt-0.5" />
                        : <ShieldAlert className="w-4 h-4 text-[#F39C12] shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <p className="text-sm" style={{ color: sel.sello.verificado_2_fuentes ? "#4ECDC4" : "#F39C12" }}>
                          {sel.sello.verificado_2_fuentes
                            ? (sel.sello.texto || "Verificado en 2 fuentes")
                            : "1 fuente oficial · verificación de 2ª fuente en curso"}
                        </p>
                        {(sel.sello.fuentes || []).length > 0 && (
                          <p className="text-xs text-[#A1A1AA] mt-1">{(sel.sello.fuentes || []).join(" + ")}</p>
                        )}
                        {sel.sello.verificado_at && (
                          <p className="text-[10px] text-[#71717A] font-mono-ax mt-1">Verificado: {fmtFecha(sel.sello.verificado_at)}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#52525b] text-xs">— Verificación no provista por el puente</p>
                  )}
                </div>

                <div className="pt-2 border-t border-[#1F1F24]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Última combinación ganadora</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(sel.ultimo_sorteo?.numeros || []).map((n, i) => <Ball key={i} n={n} />)}
                    {sel.ultimo_sorteo?.extra != null && <Ball n={sel.ultimo_sorteo.extra} extra />}
                    {faltaExtra(sel) && <BallFaltante juego={sel} />}
                    {!(sel.ultimo_sorteo?.numeros || []).length && <span className="text-[#52525b]">—</span>}
                  </div>
                  {faltaExtra(sel) && (
                    <p data-testid="expediente-falta-extra" className="text-xs text-[#F39C12] mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Falta {nombreExtra(sel)} — la app no la tiene registrada en este sorteo.
                    </p>
                  )}
                  <p className="text-xs text-[#71717A] mt-2">
                    {sel.ultimo_sorteo?.draw_date || "—"} · {sel.ultimo_sorteo?.draw_time || ""}
                  </p>
                </div>

                <div className="pt-2 border-t border-[#1F1F24]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Historial del scrape</p>
                  <div className="space-y-1 text-xs text-[#A1A1AA]">
                    <div className="flex justify-between"><span>Fuente usada</span><span className="text-[#4ECDC4]">{sel.ultimo_sorteo?.fuente || "—"}</span></div>
                    <div className="flex justify-between"><span>Último scrape</span><span className="font-mono-ax">{fmtFecha(sel.ultimo_sorteo?.scraped_at)}</span></div>
                    <div className="flex justify-between"><span>En cuarentena 48h</span><span>{sel.cuarentena_48h ? "Sí" : "No"}</span></div>
                    <div className="flex justify-between"><span>Días de sorteo</span><span>{(sel.dias_de_sorteo || []).join(", ") || "—"}</span></div>
                  </div>
                </div>

                <div className="pt-2 border-t border-[#1F1F24]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Reparto de premios del último sorteo</p>
                  <p className="text-[#52525b] text-xs">{sel.reparto_premios ? "" : "— No provisto por el puente"}</p>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-[#A1A1AA]"><Icon className="w-4 h-4 text-[#71717A]" />{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

export default Matriz;
